import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, Loader2, FileWarning } from 'lucide-react';
import {
  getAttachmentPreviewKind,
  TEXT_PREVIEW_MAX_BYTES,
  type PreviewableAttachment,
} from '@/utils/attachmentPreview';
import './AttachmentPreviewModal.css';

interface AttachmentPreviewModalProps {
  attachment: PreviewableAttachment | null;
  open: boolean;
  onClose: () => void;
  fetchBlob: (attachment: PreviewableAttachment, onProgress?: (value: number) => void) => Promise<Blob>;
  onDownload: (attachment: PreviewableAttachment, onProgress: (value: number) => void) => Promise<void>;
  formatFileSize?: (bytes: number) => string;
}

const defaultFormatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const AttachmentPreviewModal: React.FC<AttachmentPreviewModalProps> = ({
  attachment,
  open,
  onClose,
  fetchBlob,
  onDownload,
  formatFileSize = defaultFormatFileSize,
}) => {
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [mimeType, setMimeType] = useState<string>('');
  const blobUrlRef = useRef<string | null>(null);

  const previewKind = attachment
    ? getAttachmentPreviewKind(attachment.content_type, attachment.filename)
    : 'unsupported';

  const revokeBlobUrl = () => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    setBlobUrl(null);
  };

  const resetPreviewState = () => {
    revokeBlobUrl();
    setTextContent(null);
    setError(null);
    setLoading(false);
    setDownloading(false);
    setDownloadProgress(0);
    setMimeType('');
  };

  useEffect(() => {
    if (!open || !attachment) {
      resetPreviewState();
      return;
    }

    let cancelled = false;

    const loadPreview = async () => {
      setLoading(true);
      setError(null);
      setTextContent(null);

      try {
        const blob = await fetchBlob(attachment);
        if (cancelled) return;

        const type = blob.type || attachment.content_type || 'application/octet-stream';
        setMimeType(type);

        if (previewKind === 'text') {
          if (blob.size > TEXT_PREVIEW_MAX_BYTES) {
            setError(`File is too large to preview (${formatFileSize(blob.size)}). Please download it instead.`);
            return;
          }
          const text = await blob.text();
          if (!cancelled) setTextContent(text);
          return;
        }

        const url = URL.createObjectURL(blob);
        if (!cancelled) {
          blobUrlRef.current = url;
          setBlobUrl(url);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load preview');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (previewKind === 'unsupported') {
      setLoading(false);
      setError(null);
      return;
    }

    loadPreview();

    return () => {
      cancelled = true;
      revokeBlobUrl();
    };
  }, [open, attachment, fetchBlob, previewKind, formatFileSize]);

  useEffect(() => {
    if (!open) resetPreviewState();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  const handleDownload = async () => {
    if (!attachment || downloading) return;

    try {
      setDownloading(true);
      setDownloadProgress(0);
      await onDownload(attachment, setDownloadProgress);
    } finally {
      setDownloading(false);
      setDownloadProgress(0);
    }
  };

  if (!open || !attachment || typeof document === 'undefined') return null;

  const renderPreview = () => {
    if (previewKind === 'unsupported') {
      return (
        <div className="attachment-preview__unsupported">
          <FileWarning size={48} strokeWidth={1.5} />
          <p>Preview is not available for this file type.</p>
          <span>{attachment.content_type || 'Unknown type'}</span>
        </div>
      );
    }

    if (loading) {
      return (
        <div className="attachment-preview__loading">
          <Loader2 size={40} className="attachment-preview__spinner" />
          <p>Loading preview…</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="attachment-preview__unsupported">
          <FileWarning size={48} strokeWidth={1.5} />
          <p>{error}</p>
        </div>
      );
    }

    switch (previewKind) {
      case 'image':
        return blobUrl ? (
          <img
            src={blobUrl}
            alt={attachment.filename}
            className="attachment-preview__image"
          />
        ) : null;

      case 'pdf':
        return blobUrl ? (
          <iframe
            src={blobUrl}
            title={attachment.filename}
            className="attachment-preview__pdf"
          />
        ) : null;

      case 'video':
        return blobUrl ? (
          <video
            src={blobUrl}
            controls
            className="attachment-preview__video"
          >
            Your browser does not support video playback.
          </video>
        ) : null;

      case 'audio':
        return blobUrl ? (
          <div className="attachment-preview__audio-wrap">
            <audio src={blobUrl} controls className="attachment-preview__audio">
              Your browser does not support audio playback.
            </audio>
          </div>
        ) : null;

      case 'text':
        return (
          <pre className="attachment-preview__text">{textContent ?? ''}</pre>
        );

      default:
        return null;
    }
  };

  return createPortal(
    <div
      className="attachment-preview__overlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="attachment-preview__modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Preview ${attachment.filename}`}
      >
        <header className="attachment-preview__header">
          <div className="attachment-preview__meta">
            <h2 className="attachment-preview__title">{attachment.filename}</h2>
            {attachment.size != null && (
              <span className="attachment-preview__size">{formatFileSize(attachment.size)}</span>
            )}
          </div>
          <div className="attachment-preview__actions">
            <button
              type="button"
              className="attachment-preview__btn attachment-preview__btn--primary"
              onClick={handleDownload}
              disabled={downloading}
            >
              {downloading ? (
                <Loader2 size={16} className="attachment-preview__spinner" />
              ) : (
                <Download size={16} />
              )}
              {downloading
                ? downloadProgress > 0
                  ? `Downloading ${Math.round(downloadProgress)}%`
                  : 'Downloading…'
                : 'Download'}
            </button>
            <button
              type="button"
              className="attachment-preview__btn"
              onClick={onClose}
              aria-label="Close preview"
            >
              <X size={18} />
            </button>
          </div>
        </header>

        <div className="attachment-preview__body">
          {renderPreview()}
        </div>

        {mimeType && previewKind !== 'unsupported' && !loading && !error && (
          <footer className="attachment-preview__footer">
            {mimeType}
          </footer>
        )}
      </div>
    </div>,
    document.body
  );
};

export default AttachmentPreviewModal;
